
import { toast } from "sonner";
import { endNavigation } from "../navigationMonitor";
import { ErrorEntry, ErrorReportingService } from "@/components/diagnostics/types";
import { checkForSecurityImplications } from "./securityErrorDetection";

/**
 * Configure global error handling to catch unhandled errors
 */
export const configureGlobalErrorHandling = () => {
  // Track errors for reporting
  const errorLog: ErrorEntry[] = [];
  
  // Security monitoring - track suspicious errors
  const securityIncidents: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    timestamp: Date;
    details: any;
  }> = [];
  
  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    console.error("Unhandled Promise Rejection:", error);
    
    // Check for security implications
    const isSecurityIssue = checkForSecurityImplications(error, securityIncidents);
    
    // Track the error
    errorLog.push({
      message: error instanceof Error ? error.message : String(error),
      timestamp: new Date(),
      stack: error instanceof Error ? error.stack : undefined,
      severity: isSecurityIssue ? 'error' : 'warning'
    });
    
    // Make sure loading indicators are cleared
    if (window._loadingIndicator) {
      window._loadingIndicator.endAllRequests();
    }
    
    // Show a user-friendly toast
    toast.error("An unexpected error occurred. Please try again.");
    
    // Prevent the default browser behavior of logging to console
    event.preventDefault();
    
    // End any stuck navigations
    endNavigation();
  });
  
  // Handle runtime errors
  window.addEventListener("error", (event) => {
    console.error("Runtime Error:", event.error);
    
    // Check for security implications
    const isSecurityIssue = checkForSecurityImplications(event.error, securityIncidents);
    
    // Track the error
    errorLog.push({
      message: event.message,
      timestamp: new Date(),
      stack: event.error?.stack,
      severity: isSecurityIssue ? 'error' : 'warning'
    });
    
    // Make sure loading indicators are cleared
    if (window._loadingIndicator) {
      window._loadingIndicator.endAllRequests();
    }
    
    // Show a user-friendly toast
    toast.error("An unexpected error occurred. Please try again.");
    
    // End any stuck navigations
    endNavigation();
    
    // Don't prevent default so the error still appears in console
  });
  
  // Add shutdown handler
  window.addEventListener('beforeunload', () => {
    // Clean up any loading states or navigation trackers
    if (window._loadingIndicator) {
      window._loadingIndicator.endAllRequests();
    }
    
    // End any stuck navigations
    endNavigation();
    
    // If there are any high severity security incidents, log them before closing
    const highSeverityIncidents = securityIncidents.filter(i => i.severity === 'high');
    if (highSeverityIncidents.length > 0) {
      console.warn(`[SECURITY] ${highSeverityIncidents.length} high severity incidents occurred this session`);
      // In a real app, this would send a final report to a security monitoring service
    }
  });
  
  // Define the error reporting service with security incidents method
  const errorReporting: ErrorReportingService = {
    getErrors: () => [...errorLog],
    clearErrors: () => {
      errorLog.length = 0;
    },
    addError: (error: Error | string, componentName?: string, severity: 'error' | 'warning' | 'info' = 'error', additionalData?: Record<string, any>) => {
      const errorEntry: ErrorEntry = {
        message: typeof error === 'string' ? error : error.message,
        stack: typeof error !== 'string' ? error.stack : undefined,
        timestamp: new Date(),
        componentName,
        severity,
        additionalData
      };
      
      // Check for security implications
      checkForSecurityImplications(error, securityIncidents);
      
      errorLog.push(errorEntry);
      
      console.error(`[${severity.toUpperCase()}] ${componentName ? `[${componentName}] ` : ''}${errorEntry.message}`, error);
    },
    getSecurityIncidents: () => [...securityIncidents]
  };
  
  // Assign to window
  window._errorReporting = errorReporting;
  
  console.log("Global error handling and security monitoring configured");
};

// We no longer need this declaration as it's already in types.d.ts
// and we've added the getSecurityIncidents method to the base interface
